import type { AuctionBid, Hand, Position } from './problems';

/**
 * Wire types for "Predicted Jeff's Bids by AI" (feature 025-ai-predicted-bids; categories and
 * sub-categories since 032-ai-prediction-categories) — the Data Editing surface that puts the AI's
 * predicted auction next to the one Jeff actually bid. Read-mostly: its one editor write is the
 * shared correction explanation on mismatched hands.
 *
 * A prediction is identified by its SUB-CATEGORY — the (category, sub-category) pair, one id —
 * plus the deal. The same deal may carry one prediction per sub-category, so the index holds one
 * entry per prediction, not per deal.
 */

/** How a predicted call compared with what Jeff really bid. */
export type PredictedBidCorrectness =
  /** Before the divergence — engine and Jeff agreed. */
  | 'correct'
  /** The call where the engine first differed. */
  | 'incorrect'
  /**
   * After the divergence. Not "wrong": by this point the engine was answering a different
   * auction from the one Jeff was in, so the calls are not comparable at all.
   */
  | 'not_comparable'
  /**
   * The engine had no rule covering the position and declined to predict — it recorded
   * `(no rule)` rather than guessing. Not "wrong" either: there is no prediction here to be
   * wrong. Derived by the server from the bid token; the database stores these as `incorrect`,
   * which is exactly the conflation this value exists to undo.
   */
  | 'declined';

/**
 * The prediction-outcome filter.
 *
 * Amended 2026-08-20: the views mirror the three outcomes the hand badge states, so a reviewer can
 * filter to whatever the badge told them. `'mismatched'` narrowed in the same pass — it now means
 * the AI offered a call Jeff did not make, and no longer sweeps in the hands it declined to
 * predict. Those were 69% of all divergence, which made "everything that is not an exact match"
 * useless for finding a real disagreement.
 */
export type PredictionOutcomeFilter = 'all' | 'matched' | 'mismatched' | 'declined';

/** One sub-category of a category, in the client's wording. `key` is the loader's stable identifier, never shown. */
export interface PredictionSubcategory {
  /** The (category, sub-category) pair id — what every prediction, correction and bookmark refers to. */
  id: number;
  /** "1.2", "5.7" … */
  number: string;
  name: string;
  key: string;
}

/** One of the six categories, with its sub-categories in display order. */
export interface PredictionCategory {
  id: number;
  number: number;
  name: string;
  subcategories: PredictionSubcategory[];
}

/**
 * One entry in the index the page loads once — one per PREDICTION. Small on purpose: it holds
 * every prediction so filtering, jumping and the progress counter never wait on the network.
 */
export interface PredictedHandIndexItem {
  /** The sub-category this prediction is shown under. */
  subcategoryId: number;
  tableResultId: number;
  /** True when the predicted auction reproduced Jeff's bidding exactly. */
  isExactMatch: boolean;
  /**
   * True when the divergence is the engine DECLINING to predict — `(no rule)` at the first
   * divergence — rather than offering a call Jeff did not make. Always false on an exact match.
   * Carried here so the outcome filter can tell the two apart without fetching hand detail.
   */
  divergenceIsDeclined: boolean;
}

export interface PredictedHandIndexResponse {
  /** The six categories in display order, each with its sub-categories in display order. */
  categories: PredictionCategory[];
  /** Every loaded prediction, ordered by category, sub-category, then deal. */
  hands: PredictedHandIndexItem[];
}

/** A predicted Jeff call, with the engine's justification and its verdict. */
export interface PredictedJeffBid {
  /** 1-based position among Jeff's own predicted calls. */
  ordinal: number;
  bid: string;
  /** Null when the engine recorded none — the page says so rather than showing stale text. */
  reason: string | null;
  correctness: PredictedBidCorrectness;
}

/**
 * One call in the predicted auction, in bidding order across all four seats.
 * `jeffBidOrdinal` is null for partner and opponent calls: the engine reasoned about those only
 * as context and recorded no justification for them.
 */
export interface PredictedAuctionEntry {
  seat: Position;
  bid: string;
  jeffBidOrdinal: number | null;
}

/** Where and why the prediction first parted company with Jeff. Absent on exact matches. */
export interface PredictionDivergence {
  /**
   * 1-based ordinal of Jeff's first divergent call. May be one past the last predicted bid,
   * which is how "the engine stopped before the real auction did" is recorded.
   */
  firstDiffJeffCallNo: number;
  /** What Jeff actually called. The literal `(none)` means his auction had ended. */
  actualBidAtDiff: string | null;
  /** What the engine predicted. The literal `(none)` means it stopped early. */
  predictedBidAtDiff: string | null;
  cause: string | null;
  engineReasonAtDiff: string | null;
}

/**
 * Everything the review page shows for one prediction. The deal-derived fields are nullable as a
 * group: when the tournament database cannot be reached the prediction and its reasoning still
 * render, which is the bulk of what this surface is for.
 */
export interface PredictedHandDetail {
  /** The sub-category this prediction is shown under. */
  subcategoryId: number;
  /** The category's display name, for the status line. */
  categoryName: string;
  /** The sub-category's display name, for the status line. */
  subcategoryName: string;
  tableResultId: number;
  scoringType: string | null;
  tournamentTitle: string | null;
  dealer: string | null;
  vulnerability: string | null;
  jeffSeat: Position;
  jeffHand: Hand | null;
  partnerSeat: Position;
  partnerPlayer: string | null;
  partnerHand: Hand | null;
  /** Jeff's real auction, carrying each call's stored BBO alert. */
  actualAuction: AuctionBid[];
  predictedAuction: PredictedAuctionEntry[];
  predictedJeffBids: PredictedJeffBid[];
  /** Partner's predicted calls as a display string; the engine gave no reasoning for them. */
  partnerPredictedBids: string | null;
  isExactMatch: boolean;
  /**
   * True when the hand's divergence is the engine DECLINING to predict — the call at the first
   * divergence is `(no rule)` — rather than predicting something Jeff did not bid. Always false
   * on an exact match. Keeps "did not predict" from being badged as "differed from Jeff".
   */
  divergenceIsDeclined: boolean;
  divergence: PredictionDivergence | null;
  /** Pipe-separated caveats the engine recorded for this hand. */
  flagsAssumptions: string | null;
  /**
   * The editor-authored correction for this prediction's divergence, shared across all
   * reviewers. Null when none has been written; always null on an exact match (nothing to correct).
   */
  correctionExplanation: string | null;
}

/**
 * The reviewer's bookmark for one filter combination — the hand they were on and how the set was
 * narrowed. Sent as the PUT body; the scope it belongs to is `categoryId` plus `subcategoryId`
 * (null = all sub-categories of that category). There is no all-categories scope.
 */
export interface PredictionReviewState {
  lastTableResultId: number | null;
  categoryId: number;
  subcategoryId: number | null;
  outcomeFilter: PredictionOutcomeFilter;
}

/** A stored bookmark on the way back out: a {@link PredictionReviewState} plus when it was written. */
export interface ReviewStateItem extends PredictionReviewState {
  /** ISO timestamp; the newest across the list is the scope the page reopens at. */
  updatedAt: string;
}

/**
 * The reviewer's bookmarks, one per filter combination they have visited, newest-touched first.
 * The page reopens at `states[0]` and keeps the rest to resume each scope when a dropdown is
 * switched.
 */
export interface ReviewStateResponse {
  states: ReviewStateItem[];
}

/** The literal the analysis files use where a side had no call to make. */
export const NO_CALL_TOKEN = '(none)';

export const OUTCOME_FILTER_LABELS: Record<PredictionOutcomeFilter, string> = {
  all: 'All hands',
  matched: 'AI matched Jeff',
  mismatched: 'AI differed from Jeff',
  declined: 'AI had no rule',
};
